import { isUndefined } from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import {
  selectMarketAdjustedSalePrice,
  selectMarketAdjustedSalePrices,
} from './calculatedMarketAdjustmentSelectors/selectMarketAdjustedSalePrice.ts';
import { selectCompSequence } from './orderSelectors.ts';
import { selectSiteValue, selectSiteValues } from './selectSiteValue.ts';
import { selectSubjectArvValue, selectSubjectValue } from './selectSubjectValue.ts';

const calculateSiteValueRatio = (
  siteValue?: ReturnType<typeof selectSiteValue>,
  marketAdjustedSalesPrice?: ReturnType<typeof selectMarketAdjustedSalePrice>,
) => {
  if (!siteValue || !marketAdjustedSalesPrice || marketAdjustedSalesPrice.unitAmount === 0) {
    return;
  }

  return siteValue.ratio(marketAdjustedSalesPrice);
};

const formatSiteValueRatio = (siteValueRatio?: number) => {
  if (siteValueRatio === Infinity || isUndefined(siteValueRatio)) return;
  return `${Math.round(siteValueRatio * 100)}%`;
};

const selectSiteValueRatio = createSelector(
  [selectSiteValue, selectMarketAdjustedSalePrice],
  calculateSiteValueRatio,
);

const selectSubjectSiteValueRatio = createSelector(
  [selectSiteValue, selectSubjectValue],
  calculateSiteValueRatio,
);

const selectSubjectArvSiteValueRatio = createSelector(
  [selectSiteValue, selectSubjectArvValue],
  calculateSiteValueRatio,
);

export const selectSubjectSiteValueRatioDisplay = createSelector(
  [selectSubjectSiteValueRatio],
  (subjectSiteValueRatio) => formatSiteValueRatio(subjectSiteValueRatio),
);

export const selectSubjectArvSiteValueRatioDisplay = createSelector(
  [selectSubjectArvSiteValueRatio],
  (subjectArvSiteValueRatio) => formatSiteValueRatio(subjectArvSiteValueRatio),
);

export const selectSiteValueRatios = createSelector(
  [selectSiteValues, selectMarketAdjustedSalePrices, selectCompSequence, selectSubjectValue],
  (siteValues, marketAdjustedSalesPrices, compSequence, subjectValue) => {
    const properties = ['subject', ...compSequence];

    return Object.fromEntries(
      properties.map((propID) => [
        propID,
        calculateSiteValueRatio(
          siteValues[propID],
          propID === 'subject' ? subjectValue : marketAdjustedSalesPrices[propID],
        ),
      ]),
    );
  },
);

export const selectSiteValueRatioDisplay = createSelector(
  [selectSiteValueRatio],
  (siteValueRatio) => formatSiteValueRatio(siteValueRatio),
);

export const selectSiteValueRatiosDisplay = createSelector(
  [selectSiteValueRatios],
  (siteValueRatios) =>
    Object.fromEntries(
      Object.entries(siteValueRatios).map(([propID, siteValueRatio]) => {
        if (siteValueRatio === Infinity || isUndefined(siteValueRatio)) return [propID, undefined];
        return [propID, `${Math.round(siteValueRatio * 100)}%`];
      }),
    ),
);
